import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  FileText, 
  Shield, 
  Heart,
  BookOpen,
  Upload,
  ExternalLink,
  ChevronRight,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const TRAINING_TOPICS = [
  { value: 'child_growth_development', label: 'Child Growth & Development', icon: '👶', hours: 2 },
  { value: 'supervision_safety', label: 'Supervision & Safety', icon: '👁️', hours: 2 },
  { value: 'guidance_positive_interaction', label: 'Guidance & Positive Interaction', icon: '💬', hours: 1.5 },
  { value: 'health_safety_communicable_disease', label: 'Health & Disease Control', icon: '🏥', hours: 1.5 },
  { value: 'abuse_neglect_reporting', label: 'Abuse/Neglect Reporting', icon: '⚠️', hours: 1 },
  { value: 'safe_sleep_sids', label: 'Safe Sleep & SIDS', icon: '😴', hours: 1 }
];

const TRAINING_PROVIDERS = [
  { name: 'Texas A&M AgriLife - Free Annual Training', url: 'https://childcare.tamu.edu/courses/texas-annual-training/' },
  { name: 'ChildCareED Texas', url: 'https://www.childcareed.com/texas' },
  { name: 'Texas DFPS Training', url: 'https://www.dfps.state.tx.us/Training/' },
  { name: 'Care Courses', url: 'https://www.carecourses.com/' },
  { name: '360training', url: 'https://www.360training.com/childcare-training' }
];

const StaffOnboarding = () => {
  const { staffId } = useParams();
  const [staff, setStaff] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [trainingLogs, setTrainingLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);

  const [trainingForm, setTrainingForm] = useState({
    training_title: '',
    topic: '',
    hours: '',
    provider: '',
    training_date: new Date().toISOString().split('T')[0],
    certificate_url: '',
    is_8hr_preservice: true,
    notes: ''
  });

  useEffect(() => {
    fetchOnboardingData();
  }, [staffId]);

  const fetchOnboardingData = async () => {
    try {
      // Get staff info
      const staffRes = await axios.get('/staff');
      const staffMember = staffRes.data.find(s => s.id === staffId);
      setStaff(staffMember);

      // Get onboarding record
      try {
        const onboardingRes = await axios.get(`/staff-onboarding/${staffId}`);
        setOnboarding(onboardingRes.data);
      } catch (e) {
        // No onboarding record - create one
        setOnboarding(null);
      }

      // Get training logs
      try {
        const logsRes = await axios.get(`/preservice-training-logs/${staffId}`);
        setTrainingLogs(logsRes.data);
      } catch (e) {
        setTrainingLogs([]);
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const createOnboarding = async () => {
    try {
      await axios.post('/staff-onboarding', {
        staff_id: staffId,
        hire_date: new Date().toISOString().split('T')[0],
        food_handler_required: false
      });
      toast.success('Onboarding record created');
      fetchOnboardingData();
    } catch (error) {
      console.error('Error creating onboarding:', error);
      toast.error('Failed to create onboarding record');
    }
  };

  const handleLogTraining = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/preservice-training-logs', {
        staff_id: staffId,
        training_title: trainingForm.training_title,
        topic: trainingForm.topic,
        hours: parseFloat(trainingForm.hours),
        provider: trainingForm.provider,
        training_date: trainingForm.training_date,
        certificate_url: trainingForm.certificate_url || null,
        is_8hr_preservice: trainingForm.is_8hr_preservice,
        notes: trainingForm.notes || null
      });
      toast.success('Training logged successfully!');
      setShowTrainingModal(false);
      setTrainingForm({
        training_title: '',
        topic: '',
        hours: '',
        provider: '',
        training_date: new Date().toISOString().split('T')[0],
        certificate_url: '',
        is_8hr_preservice: true,
        notes: ''
      });
      fetchOnboardingData();
    } catch (error) {
      console.error('Error logging training:', error);
      toast.error('Failed to log training');
    }
  };

  const updateOnboarding = async (field, value) => {
    try {
      await axios.patch(`/staff-onboarding/${staffId}`, { [field]: value });
      toast.success('Updated successfully');
      fetchOnboardingData();
    } catch (error) {
      console.error('Error updating onboarding:', error);
      toast.error('Failed to update');
    }
  };

  const calculate8HrProgress = () => {
    const total = trainingLogs
      .filter(log => log.is_8hr_preservice)
      .reduce((sum, log) => sum + (log.hours || 0), 0);
    return Math.min(total, 8);
  };

  const calculate16HrProgress = () => {
    const total = trainingLogs
      .filter(log => !log.is_8hr_preservice)
      .reduce((sum, log) => sum + (log.hours || 0), 0);
    return Math.min(total, 16);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Staff member not found</h3>
      </div>
    );
  }

  const progress8hr = calculate8HrProgress();
  const progress16hr = calculate16HrProgress();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {staff.name} - Onboarding
              </h1>
              <p className="text-slate-600">{staff.email}</p>
              {onboarding && (
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    onboarding.onboarding_status === 'completed' ? 'bg-green-100 text-green-700' :
                    onboarding.onboarding_status === 'overdue' ? 'bg-red-100 text-red-700' :
                    onboarding.onboarding_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {onboarding.onboarding_status === 'completed' && '✓ Onboarding Complete'}
                    {onboarding.onboarding_status === 'in_progress' && '⏳ In Progress'}
                    {onboarding.onboarding_status === 'overdue' && '⚠️ Overdue'}
                    {onboarding.onboarding_status === 'not_started' && 'Not Started'}
                  </span>
                  {onboarding.can_work_with_children && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      ✓ Can Work With Children
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <Link to={`/staff/${staffId}`}>
            <Button variant="outline">
              View Full Profile
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {!onboarding ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-600 mx-auto mb-3" />
          <h3 className="font-semibold text-amber-900 mb-2">No Onboarding Record</h3>
          <p className="text-amber-700 mb-4">Create an onboarding record to start tracking pre-service training requirements.</p>
          <Button onClick={createOnboarding}>Create Onboarding Record</Button>
        </div>
      ) : (
        <>
          {/* Pre-Service Requirements Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 8-Hour Pre-Service */}
            <div className={`bg-white rounded-xl border p-4 ${onboarding.preservice_8hr_completed ? 'border-green-200' : 'border-amber-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className={`w-5 h-5 ${onboarding.preservice_8hr_completed ? 'text-green-600' : 'text-amber-600'}`} />
                  <span className="font-semibold text-slate-900">8-Hour Pre-Service</span>
                </div>
                {onboarding.preservice_8hr_completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{progress8hr} / 8 hours</span>
                  <span className="text-slate-600">{Math.round((progress8hr / 8) * 100)}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${onboarding.preservice_8hr_completed ? 'bg-green-500' : 'bg-amber-500'}`}
                    style={{ width: `${(progress8hr / 8) * 100}%` }}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Required BEFORE first day with children</p>
            </div>

            {/* 16-Hour Remaining */}
            <div className={`bg-white rounded-xl border p-4 ${onboarding.preservice_16hr_completed ? 'border-green-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className={`w-5 h-5 ${onboarding.preservice_16hr_completed ? 'text-green-600' : 'text-slate-600'}`} />
                  <span className="font-semibold text-slate-900">16-Hour Remaining</span>
                </div>
                {onboarding.preservice_16hr_completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{progress16hr} / 16 hours</span>
                  <span className="text-slate-600">{Math.round((progress16hr / 16) * 100)}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${onboarding.preservice_16hr_completed ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${(progress16hr / 16) * 100}%` }}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Due within 90 days of hire</p>
            </div>

            {/* CPR & First Aid */}
            <div className={`bg-white rounded-xl border p-4 ${onboarding.cpr_first_aid_completed ? 'border-green-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Heart className={`w-5 h-5 ${onboarding.cpr_first_aid_completed ? 'text-green-600' : 'text-rose-600'}`} />
                  <span className="font-semibold text-slate-900">CPR & First Aid</span>
                </div>
                {onboarding.cpr_first_aid_completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                )}
              </div>
              <div className="space-y-2">
                {onboarding.cpr_first_aid_completed ? (
                  <p className="text-sm text-green-600 font-medium">✓ Completed</p>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => updateOnboarding('cpr_first_aid_completed', true)}
                  >
                    Mark Complete
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">Required within 90 days</p>
            </div>

            {/* Background Check */}
            <div className={`bg-white rounded-xl border p-4 ${onboarding.background_check_completed ? 'border-green-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${onboarding.background_check_completed ? 'text-green-600' : 'text-blue-600'}`} />
                  <span className="font-semibold text-slate-900">Background Check</span>
                </div>
                {onboarding.background_check_completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="space-y-2">
                {onboarding.background_check_completed ? (
                  <p className="text-sm text-green-600 font-medium">✓ Cleared</p>
                ) : (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => updateOnboarding('background_check_completed', true)}
                  >
                    Mark Complete
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">Required before employment</p>
            </div>
          </div>

          {/* 8-Hour Pre-Service Topics */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                8-Hour Pre-Service Training Topics
              </h2>
              <Button onClick={() => setShowTrainingModal(true)}>
                <FileText className="w-4 h-4 mr-2" />
                Log Training
              </Button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Texas requires caregivers to complete training in these core topics before working with children:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {TRAINING_TOPICS.map((topic) => {
                const completed = onboarding.preservice_8hr_topics_covered?.includes(topic.value);
                const hoursLogged = trainingLogs
                  .filter(log => log.topic === topic.value && log.is_8hr_preservice)
                  .reduce((sum, log) => sum + (log.hours || 0), 0);
                
                return (
                  <div 
                    key={topic.value}
                    className={`p-4 rounded-lg border ${completed ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{topic.icon}</span>
                      <span className="font-medium text-slate-900">{topic.label}</span>
                      {completed && <CheckCircle className="w-4 h-4 text-green-600 ml-auto" />}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{hoursLogged} / {topic.hours} hrs</span>
                      <span className={`${completed ? 'text-green-600' : 'text-slate-500'}`}>
                        {completed ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Training Resources */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Approved Training Providers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {TRAINING_PROVIDERS.map((provider) => (
                <a 
                  key={provider.name}
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-primary/30 hover:bg-primary/5 transition-colors"
                >
                  <span className="font-medium text-slate-900">{provider.name}</span>
                  <ExternalLink className="w-4 h-4 text-slate-400" />
                </a>
              ))}
            </div>
          </div>

          {/* Training History */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Training History
            </h2>
            {trainingLogs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No training logged yet</p>
            ) : (
              <div className="space-y-3">
                {trainingLogs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{log.training_title}</p>
                      <p className="text-sm text-slate-600">
                        {TRAINING_TOPICS.find(t => t.value === log.topic)?.label || log.topic} • {log.provider}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">{log.hours} hrs</p>
                      <p className="text-sm text-slate-500">{new Date(log.training_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Log Training Modal */}
      <Dialog open={showTrainingModal} onOpenChange={setShowTrainingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Pre-Service Training</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogTraining} className="space-y-4 mt-4">
            <div>
              <Label>Training Title</Label>
              <Input
                value={trainingForm.training_title}
                onChange={(e) => setTrainingForm({...trainingForm, training_title: e.target.value})}
                placeholder="e.g., Child Development Basics"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Topic</Label>
              <Select
                value={trainingForm.topic}
                onValueChange={(value) => setTrainingForm({...trainingForm, topic: value})}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select topic" />
                </SelectTrigger>
                <SelectContent>
                  {TRAINING_TOPICS.map((topic) => (
                    <SelectItem key={topic.value} value={topic.value}>
                      {topic.icon} {topic.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={trainingForm.hours}
                  onChange={(e) => setTrainingForm({...trainingForm, hours: e.target.value})}
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={trainingForm.training_date}
                  onChange={(e) => setTrainingForm({...trainingForm, training_date: e.target.value})}
                  required
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Provider</Label>
              <Select
                value={trainingForm.provider}
                onValueChange={(value) => setTrainingForm({...trainingForm, provider: value})}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {TRAINING_PROVIDERS.map((provider) => (
                    <SelectItem key={provider.name} value={provider.name}>
                      {provider.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Certificate URL (optional)</Label>
              <Input
                value={trainingForm.certificate_url}
                onChange={(e) => setTrainingForm({...trainingForm, certificate_url: e.target.value})}
                placeholder="https://..."
                className="mt-1.5"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={trainingForm.is_8hr_preservice}
                onCheckedChange={(checked) => setTrainingForm({...trainingForm, is_8hr_preservice: checked})}
              />
              <Label>Counts toward 8-Hour Pre-Service (required before first day)</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1">Log Training</Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowTrainingModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffOnboarding;
